import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getClientEmails } from '../../services/clientService';
import { formatRelativeDate } from '../../utils/relativeTime';
import {
  MAILS_LIMIT,
  STATUT_FILTRES,
  ENVIRONNEMENT_FILTRES,
  traduireVerdict,
  dateEnvoi,
  filtrerEnvois,
  compteursEnvois,
  badgesEnvoi,
  destinatairesEnvoi,
  apercuTexte,
  detailsEnvoi
} from '../../utils/mailDelivery';

/**
 * Onglet « Emails » de la fiche client (msd_admin) — LECTURE SEULE (décision DI2a-6).
 *
 * Ce que l'écran montre : le journal des e-mails RÉELLEMENT envoyés au client (collection `mail` du
 * projet `msd-portail`) — intitulé, contenu, statut d'envoi et informations utiles. C'est la seule
 * trace de ce qui est parti, et jusqu'ici elle n'était lisible que côté serveur : le support ne
 * pouvait pas répondre à « le client dit qu'il n'a rien reçu ».
 *
 * Ce que l'écran ne fait JAMAIS :
 *   · aucun bouton d'envoi (un renvoi s'écrit depuis l'onglet Domaine, et c'est un geste délibéré) ;
 *   · aucun bouton de suppression (la collection `mail` est une PREUVE) ;
 *   · aucune injection de HTML dans le panneau : le corps du mail est rendu dans une `<iframe>`
 *     `sandbox=""` — un conteneur sans aucun droit (ni script, ni formulaire, ni navigation).
 *
 * Chargement : le composant lit ses données LUI-MÊME, au montage (DI2a-9) ⇒ ouvrir une fiche dont
 * on ne regarde pas les emails ne coûte aucune lecture supplémentaire. Toute la logique de
 * décision (tri, filtres, verdicts, badges) vit dans `utils/mailDelivery.js` (module pur, testé).
 */

/** Pastille d'un verdict d'envoi — le libellé vient du module pur, jamais réinventé ici. */
const VerdictPill = ({ verdict }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${verdict.classe}`}>
    {verdict.label}
  </span>
);

/** Badge d'information d'un envoi (environnement, envoi manuel, renvoi assumé). */
const MetaBadge = ({ badge }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.classe}`}>
    {badge.label}
  </span>
);

/** Ligne « libellé / valeur » du détail technique. */
const DetailRow = ({ label, valeur, mono }) => (
  <div className="flex flex-wrap gap-x-2">
    <span className="text-xs text-base-content/60">{label} :</span>
    <span className={`text-xs text-base-content break-all ${mono ? 'font-mono' : ''}`}>{valeur}</span>
  </div>
);

/** Date lisible : relative (comme partout dans le panneau) + absolue au survol. */
const dateAffichee = (date) => {
  if (!date) return 'date non tracée';
  return formatRelativeDate(date);
};

const dateComplete = (date) => (date ? date.toLocaleString('fr-FR') : '');

/**
 * Une ligne du journal : en-tête toujours visible, détail replié par défaut (DI2a-7 option (ii)).
 * @param {Object} props
 * @param {Object} props.email - Envoi mappé (`clientService.getClientEmails`)
 * @param {boolean} props.isOpen - L'envoi est-il déplié ?
 * @param {Function} props.onToggle - Ouvre / referme cette ligne
 * @param {string} props.copie - Identifiant dont la copie vient d'aboutir (retour visuel)
 * @param {Function} props.onCopier - Copie un identifiant dans le presse-papiers
 */
const LigneEnvoi = ({ email, isOpen, onToggle, copie, onCopier }) => {
  const verdict = traduireVerdict(email.delivery);
  const badges = badgesEnvoi(email.meta);
  const destinataires = destinatairesEnvoi(email.to);
  const date = dateEnvoi(email);
  const apercu = apercuTexte(email.message.html);

  return (
    <li className="border border-base-200 rounded-lg overflow-hidden bg-base-100">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left px-4 py-3 hover:bg-base-200/50 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-base-content">
            {email.message.subject || '(sans objet)'}
          </span>
          <VerdictPill verdict={verdict} />
          {badges.map((badge) => <MetaBadge key={badge.cle} badge={badge} />)}
          <span className="ml-auto text-xs text-base-content/60" title={dateComplete(date)}>
            {dateAffichee(date)}
          </span>
        </div>
        <div className="text-xs text-base-content/60 mt-1">
          {email.meta.templateName || 'gabarit non tracé'}
          {destinataires.length > 0 && <> · destinataire : {destinataires.join(', ')}</>}
          {destinataires.length === 0 && <> · destinataire non tracé</>}
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-base-200 bg-base-200/30">
          {verdict.erreur && (
            <div className="bg-error/10 border border-error/30 rounded-lg p-3 mt-3">
              <p className="text-xs uppercase tracking-wide text-error">Erreur remontée par le serveur</p>
              <p className="text-xs font-mono text-error/90 break-all mt-1">{verdict.erreur}</p>
            </div>
          )}

          <div className="space-y-1 mt-3">
            {detailsEnvoi(email).map((ligne) => (
              <DetailRow key={ligne.label} label={ligne.label} valeur={ligne.valeur} mono={ligne.mono} />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={() => onCopier('uid', email.meta.userId)}
              disabled={!email.meta.userId}
            >
              {copie === 'uid' ? '✅ uid copié' : 'Copier l’uid du compte'}
            </button>
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={() => onCopier('doc', email.id)}
            >
              {copie === 'doc' ? '✅ id copié' : 'Copier l’id du document'}
            </button>
          </div>

          {apercu && (
            <p className="text-xs text-base-content/60 italic mt-3">Aperçu texte : {apercu}</p>
          )}

          {email.message.html ? (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wide text-base-content/60 mb-1">
                Contenu envoyé (rendu isolé — aucun script ne peut s’exécuter)
              </p>
              {/* DI2a-5 : conteneur SANS AUCUN droit (`sandbox=""`), jamais de dangerouslySetInnerHTML.
                  Le HTML reste figé (`srcDoc`), donc rien ne peut viser le panneau lui-même. */}
              <iframe
                title={`Contenu de l’email « ${email.message.subject || email.id} »`}
                srcDoc={email.message.html}
                sandbox=""
                referrerPolicy="no-referrer"
                className="w-full h-96 bg-white rounded-lg border border-base-300"
              />
            </div>
          ) : (
            <p className="text-xs text-base-content/60 mt-3">
              Ce document ne contient pas de corps HTML : rien à afficher.
            </p>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Onglet « Emails » de la fiche client.
 *
 * Lecture : `getClientEmails(uid)` au MONTAGE (DI2a-9). L'erreur de lecture est distinguée de
 * l'absence d'envoi : afficher « aucun envoi » après un refus de permission serait un mensonge
 * d'écran (l'onglet est un écran de PREUVE).
 * @param {Object} props
 * @param {string} props.uid - Identifiant du compte (doc `users/{uid}`)
 */
const EmailsTab = ({ uid }) => {
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statut, setStatut] = useState('tous');
  const [environnement, setEnvironnement] = useState('tous');
  const [expandedId, setExpandedId] = useState(null);
  const [copie, setCopie] = useState('');

  const load = useCallback(async () => {
    if (!uid) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const liste = await getClientEmails(uid);
      setEmails(liste);
    } catch (error) {
      console.warn('EmailsTab: impossible de charger les envois du client', error?.message);
      setLoadError('Impossible de charger les emails de ce client.');
      setEmails([]);
    } finally {
      setIsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const compteurs = useMemo(() => compteursEnvois(emails), [emails]);
  const visibles = useMemo(
    () => filtrerEnvois(emails, { statut, environnement }),
    [emails, statut, environnement]
  );
  const compteursVisibles = useMemo(() => compteursEnvois(visibles), [visibles]);

  const filtreActif = statut !== 'tous' || environnement !== 'tous';

  /** Copie un identifiant dans le presse-papiers (retour visuel de 2 s, jamais de blocage). */
  const copier = async (cle, valeur) => {
    if (!valeur) return;
    try {
      await navigator.clipboard.writeText(valeur);
      setCopie(cle);
      setTimeout(() => setCopie(''), 2000);
    } catch (error) {
      console.warn('EmailsTab: copie impossible', error?.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-3 text-base-content/70">Chargement des emails envoyés...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-error/10 text-error p-4 rounded-md">
        <h3 className="font-bold">Emails indisponibles</h3>
        <p className="text-sm">{loadError}</p>
        <p className="text-xs mt-1">
          Le journal « mail » est réservé aux comptes administrateurs : cette lecture a été refusée
          ou interrompue. Rien n’a été modifié.
        </p>
        <button type="button" className="btn btn-sm btn-error mt-2" onClick={load}>
          Réessayer
        </button>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-base-content/80 font-medium">Aucun envoi enregistré pour ce client.</p>
        <p className="text-sm text-base-content/60 mt-1">
          Le journal des emails est vide : aucun message n’a été adressé à ce compte, ou les envois
          sont antérieurs au rattachement des emails au compte (voir la note ci-dessous).
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-base-content">Journal des emails envoyés</h3>
          <p className="text-sm text-base-content/70 mt-1">
            {compteursVisibles.total} envoi(s) · {compteursVisibles.echecs} échec(s)
            {compteursVisibles.attente > 0 && ` · ${compteursVisibles.attente} en attente`}
            {filtreActif && <> — sur {compteurs.total} envoi(s) au total</>}
          </p>
        </div>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info/20 text-info border border-info/30">
          lecture seule
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <select
          className="select select-bordered select-sm"
          value={statut}
          onChange={(event) => setStatut(event.target.value)}
          aria-label="Filtrer par statut d’envoi"
        >
          {STATUT_FILTRES.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        <select
          className="select select-bordered select-sm"
          value={environnement}
          onChange={(event) => setEnvironnement(event.target.value)}
          aria-label="Filtrer par environnement d’envoi"
        >
          {ENVIRONNEMENT_FILTRES.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        {filtreActif && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setStatut('tous');
              setEnvironnement('tous');
            }}
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      <p className="text-xs text-base-content/60 mt-3">
        Source : journal « mail » du projet portail, filtré sur le compte du client. Les tout
        premiers envois, antérieurs au rattachement à un compte, n’apparaissent pas. Les envois
        « hors production » et « non tracés » sont ÉTIQUETÉS, jamais masqués.
      </p>

      {visibles.length === 0 ? (
        <p className="text-sm text-base-content/60 py-6 text-center">
          Aucun envoi ne correspond à ces filtres.
        </p>
      ) : (
        <ul className="space-y-2 mt-3">
          {visibles.map((email) => (
            <LigneEnvoi
              key={email.id}
              email={email}
              isOpen={expandedId === email.id}
              onToggle={() => setExpandedId(expandedId === email.id ? null : email.id)}
              copie={copie}
              onCopier={copier}
            />
          ))}
        </ul>
      )}

      <p className="text-xs text-base-content/50 mt-4">
        {`Lecture limitée à ${MAILS_LIMIT} envois par fiche. Aucune action possible depuis cet écran :`}
        {` ni envoi, ni renvoi, ni suppression.`}
      </p>
    </div>
  );
};

export default EmailsTab;
